import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client';

/**
 * Staff forms attached to one submission — 02.11 to 02.15, and 02.18.
 *
 * Read behind `submissions:read_phi`, written behind `submissions:update`. None
 * of this is ever shown to a participant: 02 is explicit that flag labels,
 * pre-screen recommendations and internal reasons stay staff-side.
 */

export interface CallRecord {
  id: string;
  calledAt: string;
  studySlug: string;
  idConfirmed: boolean;
  acute: 'none' | 'emergency' | 'unsure';
  travelNh: string;
  transport?: string | null;
  currentCare?: string | null;
  outcome: string;
  notes: string;
  nextContactOn: string;
  staffName: string;
}

export interface PrescreenRecord {
  id: string;
  studySlug: string;
  answers: Record<string, string>;
  recommendation: string;
  notes: string;
  createdAt: string;
  reviewerName: string;
}

export interface FlagRecord {
  id: string;
  category: string;
  severity: 'watch' | 'urgent-today' | 'possible-sae';
  note: string;
  notified: string;
  action: string;
  resolvedAt: string | null;
  createdAt: string;
  raisedByName: string;
}

export interface ConsentRecord {
  id: string;
  studySlug: string;
  document: string;
  irbVersion: string;
  signedOn: string;
  storage: string;
  createdAt: string;
  staffName: string;
}

/**
 * 02.18. `reasonInternal` is staff-only (07 §7) — it is never mailed, and
 * nothing here may copy it onto a surface the participant reads.
 */
export interface OutcomeRecord {
  id: string;
  code: 'not-a-match' | 'waitlist' | 'withdraw' | 'invite-to-screen' | 'enroll' | 'hold';
  reasonInternal: string;
  offer: string;
  letter: string;
  sentAt: string | null;
  createdAt: string;
  decidedByName: string;
}

/** 02.14 state for a referral; null for any other submission. */
export interface ReferralState {
  ackAt: string | null;
  outreachAt: string | null;
  clinicianUpdateOk: boolean;
  hasPatient: boolean;
  patientSuppressed: boolean;
}

/**
 * 07 §3 — a referral a status ping (C3) might be about: same clinician, and
 * the study they named first. Candidates, not an answer; the coordinator says
 * which one, because answering about the wrong one means telling a clinician
 * about someone else's patient.
 */
export interface ReferralCandidate {
  submissionId: string;
  studySlug: string | null;
  matchesStudy: boolean;
  sentAt: string;
  ackAt: string | null;
  outreachAt: string | null;
  status: string;
  patientName: string | null;
}

export interface StaffRecords {
  calls: CallRecord[];
  prescreens: PrescreenRecord[];
  flags: FlagRecord[];
  consents: ConsentRecord[];
  outcomes: OutcomeRecord[];
  referral: ReferralState | null;
  /** Empty for every form but a referral-status ping. */
  referralCandidates: ReferralCandidate[];
  counts: { calls: number; prescreens: number; flags: number; openFlags: number; consents: number };
}

/** Option lists, slugged from 02's own field tables. */
export const CALL_OUTCOMES = [
  ['offer-screen', 'Offer clinic screen'],
  ['think-it-over', 'Think it over'],
  ['waitlist', 'Waitlist'],
  ['not-a-match', 'Not a match'],
  ['crisis', 'Crisis path'],
  ['no-answer', 'No answer / reschedule'],
] as const;

export const ACUTE_OPTIONS = [
  ['none', 'No concern'],
  ['emergency', 'Yes — 988 / emergency'],
  ['unsure', 'Unsure, clinician flagged'],
] as const;

export const YES_NO_UNSURE = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['unsure', 'Unsure'],
] as const;

export const RECOMMENDATIONS = [
  ['invite-screen', 'Invite to clinic screen'],
  ['request-records', 'Request records'],
  ['not-a-match', 'Not a match'],
  ['hold', 'Hold'],
] as const;

export const FLAG_CATEGORIES = [
  ['acute-suicidality', 'Acute suicidality'],
  ['medical', 'Medical'],
  ['medication', 'Medication'],
  ['psychosis-spectrum', 'Psychosis-spectrum'],
  ['cardiovascular', 'Cardiovascular'],
  ['other', 'Other'],
] as const;

export const FLAG_SEVERITIES = [
  ['watch', 'Watch'],
  ['urgent-today', 'Urgent today'],
  ['possible-sae', 'Possible SAE'],
] as const;

export const FLAG_NOTIFIED = [
  ['covering-clinician', 'Covering clinician'],
  ['pi', 'PI'],
  ['both', 'Both'],
] as const;

export const FLAG_ACTIONS = [
  ['continue', 'Continue'],
  ['pause-visits', 'Pause visits'],
  ['refer-to-care', 'Refer to care'],
  ['emergency', 'Emergency'],
  ['file-ae', 'File AE'],
] as const;

/** 02.18's three decisions — the form offers these and no others. */
export const OUTCOME_CODES = [
  ['not-a-match', 'Not a match'],
  ['waitlist', 'Waitlist'],
  ['withdraw', 'Withdrew before screen'],
] as const;

export const OUTCOME_REASONS = [
  ['safety', 'Safety'],
  ['logistics', 'Logistics'],
  ['inclusion-not-met', 'Inclusion not met'],
  ['quota', 'Quota'],
  ['other', 'Other'],
] as const;

export const OUTCOME_OFFERS = [
  ['none', 'None'],
  ['transdiagnostic', 'Transdiagnostic'],
  ['oud', 'OUD'],
  ['psi-pd', 'PSI-PD'],
  ['early-interest', 'Early interest'],
] as const;

export const OUTCOME_LETTERS = [
  ['not-a-match', 'Not a match'],
  ['waitlist', 'Waitlist'],
  ['withdrew', 'Withdrew'],
] as const;

export const CONSENT_DOCS = [
  ['screening-icf', 'Screening ICF'],
  ['study-icf', 'Study ICF'],
  ['hipaa', 'HIPAA authorization'],
  ['reconsent', 'Reconsent'],
  ['other', 'Other'],
] as const;

/** The question each worksheet asks, straight from 02.12a–c. */
export const PRESCREEN_FIELDS: Record<
  string,
  Array<{ key: string; label: string; helper?: string; options: ReadonlyArray<readonly [string, string]>; required: boolean }>
> = {
  transdiagnostic: [
    { key: 'psx.impairment', label: 'Functional impairment in mood, anxiety, trauma, OCD or addictive symptoms (coarse)', helper: 'Not a diagnosis', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: true },
    { key: 'psx.comorbid', label: 'Comorbidity present', helper: 'Allowed in this protocol', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: false },
    { key: 'psx.service', label: 'Veteran or first responder', helper: 'Focus, not a gate', options: [['veteran', 'Veteran'], ['first-responder', 'First responder'], ['neither', 'Neither']], required: false },
    { key: 'flag.physio', label: 'Physiological safety issues apparent', helper: 'Alert only', options: [['none', 'None flagged'], ['flag', 'Flag for clinician'], ['unclear', 'Unclear']], required: true },
    { key: 'flag.psych', label: 'Psychological safety issues apparent', helper: 'Alert only', options: [['none', 'None flagged'], ['flag', 'Flag for clinician'], ['unclear', 'Unclear']], required: true },
  ],
  oud: [
    { key: 'psx.oud', label: 'Opioid use disorder history (coarse)', helper: 'Diagnoses are clinical, not this form', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: true },
    { key: 'psx.oud_program', label: 'Currently in a treatment program', helper: 'Do not list MOUD names at this step', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: false },
    { key: 'flag.physio', label: 'Physiological safety flag', helper: 'Alert', options: [['none', 'None'], ['flag', 'Flag'], ['unclear', 'Unclear']], required: true },
  ],
  'psi-pd': [
    { key: 'psx.pd', label: "Parkinson's diagnosis documented", options: [['yes', 'Yes'], ['no', 'No'], ['pending-records', 'Pending records']], required: true },
    { key: 'psx.depression', label: 'Clinically significant depression (coarse)', helper: 'Not a PHQ auto-score on this sheet', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: true },
    { key: 'flag.motor', label: 'Motor / full-day visit concern', helper: 'Logistics + safety', options: [['yes', 'Yes'], ['no', 'No'], ['unclear', 'Unclear']], required: false },
    { key: 'logistics.care_partner', label: 'Care partner identified', options: [['yes', 'Yes'], ['no', 'No']], required: false },
  ],
};

export function useStaffRecords(id: string | null) {
  return useQuery({
    queryKey: ['staff', id],
    queryFn: () => request<StaffRecords>(`/staff/${id}/records`),
    enabled: id !== null,
  });
}

function useStaffMutation<T>(path: (id: string) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: T }) =>
      request(path(id), { method: 'POST', body }),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['staff', id] });
      // These forms move the submission's own status, so the row and the board
      // behind it are stale too.
      void qc.invalidateQueries({ queryKey: ['submission', id] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export const useLogCall = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/calls`);
export const useLogPrescreen = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/prescreens`);
export const useRaiseFlag = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/flags`);
export const useLogConsent = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/consents`);
export const useReferralAck = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/referral-ack`);
export const useRecordOutcome = () => useStaffMutation<Record<string, unknown>>((id) => `/staff/${id}/outcomes`);

/** 02.18's "Sent at" — closes the 2-business-day letter clock. */
export function useMarkOutcomeSent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, outcomeId }: { id: string; outcomeId: string }) =>
      request(`/staff/${id}/outcomes/${outcomeId}/sent`, {
        method: 'POST',
        body: { sentAt: new Date().toISOString() },
      }),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['staff', id] });
    },
  });
}

export function useResolveFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, flagId }: { id: string; flagId: string }) =>
      request(`/staff/${id}/flags/${flagId}/resolve`, { method: 'POST' }),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['staff', id] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
    },
  });
}

export const labelOf = (
  options: ReadonlyArray<readonly [string, string]>,
  value: string,
): string => options.find(([v]) => v === value)?.[1] ?? value;
